import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import {
  STATI, STATI_ATTIVABILI, costruisciFiltro, etichettaStato, etichettaMotivo,
  coloreStato, etichettaIdentita, riassuntoCampagna, numeroMascherato,
  spiegazioneStato, titoloStato, RIARMABILI_TESTO, eCerved, MOTIVI_CERVED,
  eGenerica, numeroDaLista,
} from './campagneData';
import CampagnaFiltri, { FILTRI_VUOTI, filtriAttivi } from '../components/CampagnaFiltri';
import CampagnaRigaDialog from '../components/CampagnaRigaDialog';
import CampagnaAzioni from '../components/CampagnaAzioni';
import CampagnaJob from '../components/CampagnaJob';
import CampagnaEliminaDialog from '../components/CampagnaEliminaDialog';
import CampagneStatistiche from '../components/CampagneStatistiche';
import { avviaBulk } from '../components/BulkProgress';
import './campagne.css';

/* Le righe delle campagne TIM: il purgatorio dei numeri mascherati.
   Il filtro nasce in UN SOLO posto — `costruisciFiltro` — e da li' va alla lista, alle
   azioni di massa e all'avvio del lavoro: e' la lezione del bug 935dd79, dove due
   traduzioni diverse dello stesso filtro facevano lavorare zero righe. */

const PAGINA = 200;
const fmt = (n) => (n ?? 0).toLocaleString('it-IT');

/* Dove va ogni azione di massa e cosa si dice dopo. Le chiavi sono quelle di
   CampagnaAzioni.AZIONI. Il corpo porta {ids} OPPURE {filtro}, mai tutt'e due: il backend
   da' la precedenza agli ids, e mandarli insieme vorrebbe dire lavorare una manciata di
   righe credendo di lavorarle tutte.

   Ogni endpoint restringe agli stati che ammette (restringi_stati), quindi "zero
   modificate" e' un esito normale, non un errore: il messaggio dice quali righe quella
   azione tocca davvero. */
const AZIONE_API = {
  accendi: { url: '/campagne/righe/attiva', extra: { attiva: true }, accende: true,
             fatto: (n) => `Indagine accesa su ${n} righe`,
             zero: 'Nessuna riga accesa: l’indagine si accende solo sulle righe da indagare e su quelle non risolte.' },
  spegni: { url: '/campagne/righe/attiva', extra: { attiva: false },
            fatto: (n) => `Indagine spenta su ${n} righe`,
            zero: 'Nessuna riga spenta: l’indagine si spegne solo sulle righe da indagare e su quelle non risolte.' },
  riindaga: { url: '/campagne/ri-indaga', extra: {}, accende: true,
              fatto: (n) => `${n} righe rimesse da indagare, con l’indagine accesa`,
              zero: 'Nessuna riga rimessa in coda: la ri-indagine vale solo sulle non risolte.' },
  riarma: { url: '/campagne/righe/riarma', extra: { attiva: true }, accende: true,
            fatto: (n) => `${n} righe riarmate: tornano da indagare, con l’indagine accesa`,
            zero: `Nessuna riga riarmata: si riarmano solo quelle ${RIARMABILI_TESTO}.` },
};

const sitoCorto = (sito) => {
  const s = String(sito || '').replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');
  return s.length > 30 ? `${s.slice(0, 29)}…` : s;
};

/* I valori di provincia e tipo impresa arrivano tali e quali dal CSV di TIM (maiuscoli:
   "TARANTO", "PERSONA GIURIDICA") e il backend li confronta ESATTI: chi scrive "Taranto"
   leggerebbe "il filtro non trova nessuna riga", che e' vero ma fuorviante. Il digitato si
   rimappa sul suggerimento che combacia a meno di maiuscole e minuscole; se non combacia
   niente si manda quello che ha scritto l'utente. */
const allinea = (valore, elenco) => {
  const v = (valore || '').trim();
  if (!v) return '';
  return (elenco || []).find((x) => x.toLowerCase() === v.toLowerCase()) || v;
};

export default function CampagnePage() {
  const [campagne, setCampagne] = useState([]);
  const [campagneStato, setCampagneStato] = useState('carico');   // 'carico' | 'ok' | 'errore'
  const [eliminaOpen, setEliminaOpen] = useState(false);
  // 'righe' (la pagina di sempre) | 'statistiche'. Il perimetro delle statistiche e' la
  // campagna scelta nel selettore in cima, lo stesso delle righe.
  const [scheda, setScheda] = useState('righe');

  const [filtri, setFiltri] = useState({ campagna_id: '', ...FILTRI_VUOTI });
  const [sugg, setSugg] = useState({ campagna: null, province: [], tipiImpresa: [] });
  const [righe, setRighe] = useState([]);
  const [totale, setTotale] = useState(0);
  const [stato, setStato] = useState('carico');   // 'carico' | 'ok' | 'errore'
  const [altre, setAltre] = useState(false);
  const [scelte, setScelte] = useState(() => new Set());
  // la selezione "tutte quelle che rispettano i filtri": e' un'altra cosa dalle righe
  // spuntate, e non si tiene in `scelte` proprio per non confonderle
  const [tuttiFiltrati, setTuttiFiltrati] = useState(false);
  const [azione, setAzione] = useState('');       // azione di massa in corso
  const [rigaAperta, setRigaAperta] = useState(null);

  // Il lavoro in sottofondo: stato del motore, quante righe partirebbero adesso, e il
  // contatore che forza la rilettura della lista dopo un'azione.
  const [job, setJob] = useState({ attivo: false, totale: 0, fatti: 0, risolti: 0 });
  const [fermando, setFermando] = useState(false);
  // L'agente Campagne (/campagne/agente): da acceso lavora da solo le righe accese di tutte
  // le campagne, e allora "Avvia il lavoro" non serve piu'.
  const [agente, setAgente] = useState(null);
  const agenteAcceso = !!agente?.attivo;
  const leggiAgente = useCallback(() => axios.get(`${API}/campagne/agente`)
    .then((r) => { setAgente(r.data || null); return r.data; })
    .catch(() => null), []);
  const [pronte, setPronte] = useState(null);     // null = non lo so ancora
  const [giro, setGiro] = useState(0);
  // il giro che rifa' SOLO il conteggio delle righe pronte: l'interruttore di riga cambia
  // quel numero senza toccare la tabella, e ricaricare tutta la lista per un interruttore
  // farebbe saltare la selezione sotto le dita
  const [giroPronte, setGiroPronte] = useState(0);
  const ricaricaRighe = useCallback(() => setGiro((g) => g + 1), []);
  const ricontaPronte = useCallback(() => setGiroPronte((g) => g + 1), []);

  // L'elenco delle campagne si ricarica dopo ogni caricamento riuscito: i contatori
  // arrivano da qui (il backend li ricalcola dalle righe, non li memorizza). Elenco
  // vuoto ed errore sono due cose DIVERSE: se il backend non risponde, dire "nessuna
  // campagna caricata" sarebbe la bugia peggiore possibile.
  // `silenzioso` serve alla rilettura durante il lavoro (vedi il battito piu' sotto): senza,
  // ogni giro rimetteva la pagina a "Carico le campagne…" facendo sparire tabella e filtri,
  // e un intoppo di rete di mezzo secondo l'avrebbe svuotata dicendo "non riesco a leggere".
  const caricaCampagne = useCallback((silenzioso) => {
    if (!silenzioso) setCampagneStato('carico');
    return axios.get(`${API}/campagne`)
      .then((r) => {
        setCampagne(r.data?.items || []);
        setCampagneStato('ok');
        // /campagne porta anche lo stato del motore: aprendo la pagina a lavoro gia'
        // avviato (magari da un'altra scheda) si vede subito, senza aspettare un battito.
        // Nella richiesta silenziosa del battito pero' si ignora: quello stato arriva
        // gia', piu' fresco, da /campagne/job (interrogato ogni due secondi), e se questa
        // risposta piu' lenta arriva dopo un attivo:false gia' visto lo farebbe tornare
        // vero per un giro, con un secondo avviso di fine lavoro di troppo.
        if (r.data?.job && !silenzioso) setJob(r.data.job);
      })
      .catch(() => {
        if (silenzioso) return;                 // si tiene quello che c'e' gia' a schermo
        setCampagne([]); setCampagneStato('errore');
      });
  }, []);
  useEffect(() => { caricaCampagne(); }, [caricaCampagne]);

  // Il vocabolario dei suggerimenti sta in uno stato SEPARATO e si aggiorna solo quando
  // quel filtro non e' attivo: ricavandolo dalle righe gia' filtrate si auto-distruggeva
  // (scelta "TARANTO", le righe sono tutte di Taranto, e la tendina resta con quella sola
  // voce). Si riscrive solo se e' davvero cambiato, altrimenti il giro
  // suggerimenti -> filtro -> righe -> suggerimenti non finirebbe mai.
  useEffect(() => {
    const distinti = (campo) => [...new Set(righe.map((r) => r[campo]).filter(Boolean))];
    setSugg((s) => {
      // cambiata campagna, il vocabolario riparte da zero: i valori dell'altra non
      // c'entrano piu'. Dentro la stessa campagna invece si somma soltanto (anche le
      // pagine successive portano valori nuovi), cosi' nessun altro filtro lo accorcia.
      const daCapo = s.campagna !== filtri.campagna_id;
      const somma = (vecchi, campo, suo) => {
        const base = daCapo ? [] : vecchi;
        return suo ? base : [...new Set([...base, ...distinti(campo)])].sort();
      };
      const province = somma(s.province, 'provincia', !!filtri.provincia);
      const tipiImpresa = somma(s.tipiImpresa, 'tipo_impresa', !!filtri.tipo_impresa);
      const uguale = !daCapo && province.join('|') === s.province.join('|')
        && tipiImpresa.join('|') === s.tipiImpresa.join('|');
      return uguale ? s : { campagna: filtri.campagna_id, province, tipiImpresa };
    });
  }, [righe, filtri.provincia, filtri.tipo_impresa, filtri.campagna_id]);

  // Il filtro per il backend: una sola traduzione, ritardata di un quarto di secondo
  // perche' la casella di ricerca non faccia una richiesta per ogni tasto premuto. Il
  // confronto sul contenuto (`chiave`) evita di rilanciare la lista quando il filtro e'
  // rinato identico — succede ad ogni aggiornamento dei suggerimenti.
  const filtro = useMemo(() => costruisciFiltro({
    ...filtri,
    provincia: allinea(filtri.provincia, sugg.province),
    tipo_impresa: allinea(filtri.tipo_impresa, sugg.tipiImpresa),
  }), [filtri, sugg]);
  const chiave = JSON.stringify(filtro);
  const [filtroVivo, setFiltroVivo] = useState(filtro);
  // mezzo secondo, non un quarto: ogni cambio di filtro costa DUE conteggi completi sulle
  // righe (la lista e le righe pronte) e la ricerca per ragione sociale e' una $regex che
  // nessun indice puo' aiutare. A 250 ms una digitazione normale ne sparava una decina.
  useEffect(() => {
    const t = setTimeout(() => setFiltroVivo((v) => (JSON.stringify(v) === chiave ? v : filtro)), 500);
    return () => clearTimeout(t);
  }, [chiave, filtro]);

  // Il filtro in corso, leggibile da dentro una richiesta gia' partita (le funzioni
  // catturano il valore di quando sono state create, il riferimento no).
  const filtroRef = useRef(filtroVivo);
  useEffect(() => { filtroRef.current = filtroVivo; }, [filtroVivo]);

  // Quante righe sono a schermo e se c'e' una selezione in corso: servono al battito del
  // lavoro (piu' sotto) e vanno letti SENZA rilanciarlo ad ogni spunta.
  const righeRef = useRef(righe);
  useEffect(() => { righeRef.current = righe; }, [righe]);
  const selezioneRef = useRef(false);
  useEffect(() => { selezioneRef.current = tuttiFiltrati || scelte.size > 0; },
    [tuttiFiltrati, scelte]);

  const chiediRighe = useCallback((skip) => axios.post(
    `${API}/campagne/righe?limit=${PAGINA}&skip=${skip}`, { filtro: filtroVivo },
  ), [filtroVivo]);

  // Cambiato il filtro si riparte dalla prima pagina. `vivo` scarta la risposta di una
  // richiesta gia' superata da un'altra: con due filtri cambiati in fretta, la piu'
  // lenta arriverebbe per ultima e mostrerebbe righe che non c'entrano piu'.
  useEffect(() => {
    let vivo = true;
    setStato('carico');
    setScelte(new Set());
    setTuttiFiltrati(false);
    chiediRighe(0)
      .then((r) => {
        if (!vivo) return;
        setRighe(r.data?.items || []);
        setTotale(r.data?.totale || 0);
        setStato('ok');
      })
      .catch(() => { if (vivo) { setRighe([]); setTotale(0); setStato('errore'); } });
    return () => { vivo = false; };
  }, [chiediRighe, giro]);

  const caricaAltre = () => {
    const suQuesto = filtroVivo;
    setAltre(true);
    chiediRighe(righe.length)
      .then((r) => {
        // stessa guardia della prima pagina: se il filtro e' cambiato mentre la richiesta
        // era in volo, accodare queste righe vorrebbe dire mescolare due elenchi diversi
        if (filtroRef.current !== suQuesto) return;
        setRighe((x) => [...x, ...(r.data?.items || [])]);
        setTotale(r.data?.totale || 0);
      })
      .catch(() => toast.error('Non sono riuscito a caricare altre righe'))
      .finally(() => setAltre(false));
  };

  /* Rilettura SILENZIOSA della tabella: stesse righe, stesso filtro, senza rimettere la
     pagina a "Carico le righe…" e senza azzerare la selezione. La usa il battito del
     lavoro, dove gli stati cambiano sotto e la tabella resterebbe congelata per ore.
     Si richiedono tante righe quante ne ha gia' a schermo (il tetto del backend e' 500). */
  const aggiornaRighe = useCallback(() => {
    /* Se si e' caricato con "Carica altre" oltre il tetto (500), la rilettura periodica
       va saltata: la richiesta e' comunque tagliata a 500 righe, e sostituendole a quelle
       gia' in tabella la lista si accorcerebbe sotto gli occhi di chi la sta leggendo,
       con tanto di scrollata che salta. Chi ha scorso cosi' tanto sta guardando una riga
       precisa, non i contatori: meglio lasciarla ferma e aggiornarla al prossimo giro
       utile (selezione, cambio filtro, fine lavoro). */
    if (righeRef.current.length > 500) return;
    const suQuesto = filtroRef.current;
    const quante = Math.min(500, Math.max(PAGINA, righeRef.current.length));
    axios.post(`${API}/campagne/righe?limit=${quante}&skip=0`, { filtro: suQuesto })
      .then((r) => {
        if (filtroRef.current !== suQuesto) return;
        setRighe(r.data?.items || []);
        setTotale(r.data?.totale || 0);
      })
      .catch(() => { /* il prossimo giro riprova: non si sporca la pagina per un intoppo */ });
  }, []);

  /* Quante righe lavorerebbe "Avvia" adesso. E' la stessa query del motore, che al filtro
     dell'utente aggiunge sempre attiva=true e stato='da_indagare' (campagne_engine._runner):
     per questo lo stato scelto nei filtri qui non conta, e il pulsante lo dice. Chiedendo
     una riga sola si legge solo il totale.

     `giroPronte` lo rifa' dopo ogni interruttore di riga: senza, su una campagna appena
     caricata (tutte le righe spente) il conteggio restava a zero, "Avvia" restava spento e
     accendere le righe una per una non lo sbloccava mai — l'unica via d'uscita era cambiare
     filtro. Mentre il lavoro gira il conteggio NON si rifa': il pulsante e' comunque
     spento, e sarebbe un conteggio completo ogni volta. */
  useEffect(() => {
    if (job.attivo) return undefined;
    let vivo = true;
    axios.post(`${API}/campagne/righe?limit=1&skip=0`,
      { filtro: { ...filtroVivo, attiva: true, stato: 'da_indagare' } })
      .then((r) => { if (vivo) setPronte(r.data?.totale || 0); })
      .catch(() => { if (vivo) setPronte(null); });
    return () => { vivo = false; };
  }, [filtroVivo, giro, giroPronte, job.attivo]);

  // Finche' il lavoro e' attivo lo si interroga ogni due secondi: l'anello nella riga in
  // alto segue il progresso, questa barra dice se si e' fermato e perche'.
  const eraAttivo = useRef(false);
  // vero quando il lavoro in corso l'abbiamo annunciato noi all'anello di progresso:
  // e' lui a dare l'avviso di fine, e due avvisi per lo stesso lavoro sono uno di troppo
  const anelloNostro = useRef(false);
  /* Ogni due secondi lo stato del motore; ogni dieci battiti (venti secondi) anche i
     CONTATORI e la tabella. Senza, sulla stessa schermata si leggeva «Indagine in corso ·
     340 di 2.140» e sotto «0 Risolta · 2.140 Da indagare» per due giorni: i contatori sono
     il dato per cui la pagina esiste, e sono anche filtri cliccabili.
     La tabella si rilegge solo se NON c'e' una selezione in corso: rifarla sotto le dita di
     chi sta spuntando righe vorrebbe dire agire su un elenco diverso da quello scelto. */
  const battiti = useRef(0);
  useEffect(() => {
    if (!job.attivo) return undefined;
    eraAttivo.current = true;
    battiti.current = 0;
    const id = setInterval(() => {
      axios.get(`${API}/campagne/job`).then((r) => setJob(r.data || {})).catch(() => {});
      battiti.current += 1;
      if (battiti.current % 10 === 0) {
        caricaCampagne(true);
        if (!selezioneRef.current) aggiornaRighe();
      }
    }, 2000);
    return () => clearInterval(id);
  }, [job.attivo, caricaCampagne, aggiornaRighe]);

  /* L'agente si interroga sempre (ogni cinque secondi): puo' essere acceso o spento dallo
     sportellino in qualunque momento. Mentre lavora (acceso e non in attesa), ogni quattro
     battiti si rileggono contatori e tabella, come fa il battito del lavoro manuale: senza,
     la pagina restava ferma mentre l'agente cambiava gli stati sotto. */
  const battitiAgente = useRef(0);
  useEffect(() => {
    leggiAgente();
    const id = setInterval(() => {
      leggiAgente().then((a) => {
        if (!a?.attivo || a.in_attesa) return;
        battitiAgente.current += 1;
        if (battitiAgente.current % 4 === 0) {
          caricaCampagne(true);
          if (!selezioneRef.current) aggiornaRighe();
        }
      });
    }, 5000);
    return () => clearInterval(id);
  }, [leggiAgente, caricaCampagne, aggiornaRighe]);

  /* A lavoro finito: contatori e righe si rileggono (gli stati sono cambiati sotto).

     L'avviso lo da' UNO solo. Se il lavoro l'abbiamo avviato noi, e' l'anello nella
     riga in alto — come per ogni altro strumento — e qui non si aggiunge niente; se
     stava gia' girando (altra scheda, o avviato prima di aprire la pagina) l'anello
     non lo segue e l'avviso tocca a noi.

     `fermo_per_blocco`, `fermo_per_errori` e `annullato` (fermato a mano) NON sono un
     successo: il motore chiude comunque con attivo=false, e chiamarla "finita" sarebbe la
     stessa bugia che la barra qui sopra evita con cura. */
  useEffect(() => {
    if (job.attivo || !eraAttivo.current) return;
    eraAttivo.current = false;
    const fermo = job.fermo_per_blocco
      ? 'Google chiede il captcha'
      : job.fermo_per_errori ? 'errori ripetuti nella lavorazione'
      : job.annullato ? 'l’hai fermato tu' : '';
    if (anelloNostro.current) {
      anelloNostro.current = false;              // l'avviso lo da' l'anello
    } else if (fermo) {
      toast.error(`Lavoro fermato (${fermo}) dopo ${fmt(job.fatti)} righe di ${fmt(job.totale)} · `
        + `${fmt(job.risolti)} numeri ricostruiti`);
    } else {
      toast.success(`Lavoro finito · ${fmt(job.risolti)} numeri ricostruiti su ${fmt(job.fatti)} righe`);
    }
    caricaCampagne();
    ricaricaRighe();
  }, [job.attivo, job.risolti, job.fatti, job.totale, job.fermo_per_blocco,
      job.fermo_per_errori, job.annullato, caricaCampagne, ricaricaRighe]);

  const campagnaScelta = useMemo(
    () => campagne.find((c) => c.id === filtri.campagna_id) || null,
    [campagne, filtri.campagna_id],
  );

  // Contatori per stato: della campagna scelta, oppure la somma di tutte.
  const contatori = useMemo(() => {
    const elenco = campagnaScelta ? [campagnaScelta] : campagne;
    const out = {};
    STATI.forEach((s) => {
      out[s.key] = elenco.reduce((n, c) => n + ((c.contatori || {})[s.key] || 0), 0);
    });
    return out;
  }, [campagne, campagnaScelta]);

  /* Liste CERVED. Il selettore del tipo compare solo se ce n'e' almeno una e non e' scelta
     una campagna; le spunte ex clienti TIM / fibra in arrivo e i motivi CERVED solo quando
     si guardano liste CERVED. Chi lavora solo liste TIM ritrova la barra com'era. */
  const ciSonoCerved = campagne.some(eCerved);
  const piuTipi = ciSonoCerved || campagne.some(eGenerica);
  const mostraTipo = piuTipi && !filtri.campagna_id;
  const mostraCerved = campagnaScelta ? eCerved(campagnaScelta)
    : mostraTipo && filtri.tipo === 'cerved';
  const motiviCerved = campagnaScelta ? eCerved(campagnaScelta)
    : ciSonoCerved && !['tim', 'generica'].includes(filtri.tipo);

  /* Un filtro nascosto non deve restare acceso: passando da una lista CERVED a una TIM con
     "ex clienti TIM" spuntato, il filtro invisibile toglierebbe ogni riga senza dire perche'.
     Solo a elenco letto: durante il caricamento (o dopo un errore) non si sa ancora. */
  useEffect(() => {
    if (campagneStato !== 'ok') return;
    if (filtri.campagna_id && !campagnaScelta) return;   // campagna appena caricata, elenco in arrivo
    const patch = {};
    if (!mostraTipo && filtri.tipo) patch.tipo = '';
    if (!mostraCerved && filtri.ex_tim) patch.ex_tim = false;
    if (!mostraCerved && filtri.ftth_nuova) patch.ftth_nuova = false;
    if (!motiviCerved && MOTIVI_CERVED.includes(filtri.motivo)) patch.motivo = '';
    if (Object.keys(patch).length) setFiltri((f) => ({ ...f, ...patch }));
  }, [campagneStato, campagnaScelta, filtri.campagna_id, mostraTipo, mostraCerved, motiviCerved,
      filtri.tipo, filtri.ex_tim, filtri.ftth_nuova, filtri.motivo]);

  const attivi = filtriAttivi(filtri);
  const azzera = () => setFiltri((f) => ({ campagna_id: f.campagna_id, ...FILTRI_VUOTI }));
  const clickStato = (key) => setFiltri((f) => ({ ...f, stato: f.stato === key ? '' : key }));

  /* La casella in testata prende le righe MOSTRATE (la pagina, 200 righe alla volta).
     "Tutte quelle che rispettano i filtri" e' un'altra cosa e si chiede a parte: sono
     migliaia e non sono a schermo. Toccare una riga a mano esce da quella selezione
     allargata, altrimenti si crederebbe di star togliendo una riga da migliaia. */
  const tuttaPagina = righe.length > 0 && righe.every((r) => scelte.has(r.id));
  const scegliTutte = (si) => {
    setTuttiFiltrati(false);
    setScelte(si ? new Set(righe.map((r) => r.id)) : new Set());
  };
  const scegli = (id) => {
    if (tuttiFiltrati) {
      // toglieva la spunta a una riga e si ritrovava selezionata solo quella: uscendo
      // dalla selezione allargata si tiene quello che si vede, meno la riga toccata
      setTuttiFiltrati(false);
      setScelte(new Set(righe.filter((r) => r.id !== id).map((r) => r.id)));
      return;
    }
    setScelte((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const annullaSelezione = () => { setTuttiFiltrati(false); setScelte(new Set()); };

  /* Un'azione di massa manda {ids} se la selezione e' esplicita, altrimenti {filtro}:
     mai tutt'e due. Dopo, la tabella e i contatori si rileggono — gli stati sono cambiati
     sotto — e la selezione si azzera: le righe toccate spesso non rientrano piu' nel
     filtro, e tenerle selezionate vorrebbe dire agire su un elenco che non esiste piu'. */
  const agisci = async (chiave) => {
    const a = AZIONE_API[chiave];
    if (!a) return;
    // Guardia: senza selezione esplicita e senza "tutte le filtrate" non si manda niente.
    // Oggi la barra non si monta nemmeno, ma {ids: []} al backend ricadrebbe sul filtro, e
    // con "Tutte le campagne" quel filtro e' vuoto: toccherebbe ogni riga di ogni campagna.
    if (!tuttiFiltrati && scelte.size === 0) return;
    setAzione(chiave);
    try {
      const corpo = tuttiFiltrati ? { filtro: filtroVivo } : { ids: [...scelte] };
      const r = await axios.post(`${API}${a.url}`, { ...corpo, ...a.extra });
      const n = r.data?.modificate || 0;
      if (!n) {
        toast.info(a.zero);
      } else {
        // Le righe accese mentre il lavoro gira entrano subito nel mulino, ma il totale
        // era stato contato all'avvio: la barra puo' leggere "250 di 200". Non e' un
        // guasto, e chi ha appena acceso deve sapere che sta allungando QUESTO lavoro.
        // Con l'agente acceso non c'e' niente da avviare: le righe le prende lui (il backend
        // lo ha appena svegliato), e il messaggio lo dice.
        const conAgente = a.accende && (r.data?.agente_attivo ?? agenteAcceso);
        const dentro = conAgente ? ' · l’agente Campagne le prende in carico'
          : job.attivo && a.accende ? ' · entrano in questo lavoro, il totale si muoverà' : '';
        toast.success(a.fatto(fmt(n)) + dentro);
      }
      annullaSelezione();
      ricaricaRighe();
      caricaCampagne();
    } catch {
      // niente risposta non vuol dire niente scritto: la richiesta puo' essere andata a
      // buon fine e essersi persa al ritorno. Si rilegge invece di affermare.
      toast.error('Non ho ricevuto risposta: rileggo l’elenco per vedere cosa è cambiato');
      annullaSelezione();
      ricaricaRighe();
      caricaCampagne();
    }
    setAzione('');
  };

  /* "Avvia" lavora le righe ACCESE e da indagare che rispettano i filtri — non la
     selezione: /campagne/bulk prende solo il filtro (gli ids li ignora), e fingere il
     contrario sarebbe la bugia peggiore di tutta la pagina. Se un lavoro era gia' in
     corso il backend risponde avviato:false: si dice, e non si annuncia nessuna partenza
     all'anello di progresso. */
  const avvia = async () => {
    setAzione('avvia');
    try {
      const r = await axios.post(`${API}/campagne/bulk`, { filtro: filtroVivo });
      const j = r.data || {};
      setJob(j);
      if (j.agente) {
        // un solo lavoratore alla volta: con l'agente acceso il lavoro manuale non parte
        toast.info('L’agente Campagne è acceso: le righe accese le lavora già lui.');
        leggiAgente();
      } else if (j.avviato) {
        anelloNostro.current = true;
        avviaBulk('campagne', { total: pronte || 0 });
        toast.success(`Lavoro avviato su ${fmt(pronte)} righe accese`);
      } else {
        toast.info('Un lavoro è già in corso: non ne è partito un altro.');
      }
    } catch {
      toast.error('Non sono riuscito ad avviare il lavoro');
    }
    setAzione('');
  };

  const ferma = async () => {
    setFermando(true);
    try {
      const r = await axios.post(`${API}/campagne/job/ferma`);
      setJob(r.data || {});
      toast.info('Interruzione richiesta: la riga in corso viene finita, poi il lavoro si ferma.');
    } catch {
      toast.error('Non sono riuscito a fermare il lavoro');
    }
    setFermando(false);
  };

  // Interruttore di riga: si accende subito e, se il backend dice di non aver modificato
  // niente, si rilegge la riga invece di indovinare. "Zero modificate" vuol dire due cose
  // diverse — l'indagine era gia' cosi' (doppio clic, o riga presa dal lavoro in corso)
  // oppure lo stato non la accetta piu' — e affermare la seconda quando era la prima
  // sarebbe un messaggio falso.
  const cambiaAttiva = async (riga, attiva) => {
    setRighe((x) => x.map((y) => (y.id === riga.id ? { ...y, attiva } : y)));
    try {
      const r = await axios.post(`${API}/campagne/righe/attiva`, { ids: [riga.id], attiva });
      // il numero sul pulsante "Avvia" dipende da questo interruttore: senza riconteggio
      // restava fermo a "0 righe accese" e il pulsante spento per sempre
      ricontaPronte();
      if (r.data?.modificate) return;
      const vera = await axios.get(`${API}/campagne/riga/${riga.id}`).then((x) => x.data);
      setRighe((x) => x.map((y) => (y.id === riga.id ? { ...y, ...vera } : y)));
      if (!!vera.attiva !== attiva) {
        toast.info(`Indagine invariata: la riga adesso è «${etichettaStato(vera.stato)}»`);
      }
    } catch {
      setRighe((x) => x.map((y) => (y.id === riga.id ? { ...y, attiva: !attiva } : y)));
      toast.error('Non sono riuscito a cambiare l’indagine');
    }
  };

  const mostraElenco = campagneStato === 'ok' && campagne.length > 0;

  return (
    <div className="camp-pagina">
      <div className="camp-head">
        <h2 className="camp-titolo">Da qualificare</h2>
        <select className="camp-scelta" value={filtri.campagna_id}
          onChange={(e) => setFiltri((f) => ({ ...f, campagna_id: e.target.value }))}>
          <option value="">Tutte le liste</option>
          {campagne.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}{eCerved(c) ? ' · CERVED' : ''}{eGenerica(c) ? ' · DA IMPORT' : ''} · {riassuntoCampagna(c.contatori) || 'nessuna riga'}
            </option>
          ))}
        </select>
        {/* Si elimina solo la campagna che stai guardando: "tutte le campagne" non e'
            una cosa che si possa cancellare per sbaglio. */}
        {campagnaScelta && (
          <button type="button" className="camp-elimina" onClick={() => setEliminaOpen(true)}
            title={`Elimina la lista «${campagnaScelta.nome}» e tutte le sue righe`}>
            Elimina
          </button>
        )}
        <div className="camp-spazio" />
        {/* "Avvia" non guarda la selezione: il motore prende le righe accese e da indagare
            che rispettano i filtri. Il numero sul pulsante e' quello vero, contato con la
            stessa query. */}
        {/* "Ferma" sta una volta sola, nella barra del lavoro qui sotto, accanto al
            progresso: due pulsanti identici a un palmo di distanza sono un dubbio inutile. */}
        {/* Con "Tutte le campagne" — che e' come la pagina si apre — il lavoro prende le
            righe accese di OGNI campagna caricata: se non lo dice il pulsante, non lo dice
            nessuno. */}
        {/* Nelle statistiche "Avvia" non si mostra: lavora le righe che rispettano i filtri,
            e i filtri li' non si vedono. */}
        {mostraElenco && scheda === 'righe' && agenteAcceso && (
          <span className="camp-box camp-box-info" title="Si spegne dallo sportellino degli agenti o dalla sua finestra">
            L’agente Campagne è acceso: lavora da solo le righe accese
          </span>
        )}
        {mostraElenco && scheda === 'righe' && !agenteAcceso && (
          <button type="button" className="btn btn-primary" onClick={avvia}
            disabled={job.attivo || azione === 'avvia' || pronte === 0}
            title={'Lavora le righe con l’indagine accesa, ancora da indagare, che '
              + 'rispettano i filtri: la selezione e lo stato scelto nei filtri non contano.'
              + (filtri.campagna_id ? '' : ' Nessuna campagna scelta: le prende da tutte.')}>
            {job.attivo ? 'Lavoro in corso…'
              : azione === 'avvia' ? 'Avvio…'
              : pronte === null ? 'Avvia il lavoro'
              : `Avvia il lavoro su ${fmt(pronte)} righe accese`
                + (filtri.campagna_id ? '' : ' di tutte le liste')}
          </button>
        )}
        {/* Le liste si caricano SOLO dall'Import: una porta sola. L'Import riconosce da
            solo i file di TIM e di CERVED e li manda qui (13/10/2026). */}
        <a className={`btn ${mostraElenco ? 'btn-secondary' : 'btn-primary'}`}
          href="/import" style={{ textDecoration: 'none' }}
          title="Le liste si caricano dall'Import: riconosce da solo i file TIM e CERVED">
          Carica una lista
        </a>
      </div>

      <CampagnaJob job={job} onFerma={ferma} fermando={fermando} agente={agente} />

      {mostraElenco && (
        <div className="camp-schede" role="tablist">
          {[['righe', 'Righe'], ['statistiche', 'Statistiche']].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={scheda === k}
              className={`camp-scheda${scheda === k ? ' on' : ''}`} onClick={() => setScheda(k)}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* `aggiorna` = l'elenco delle campagne: si rilegge dopo un caricamento, un'azione o
          durante il lavoro, e le statistiche lo seguono. */}
      {mostraElenco && scheda === 'statistiche' ? (
        <CampagneStatistiche campagnaId={filtri.campagna_id} aggiorna={campagne}
          onScegliCampagna={(id) => setFiltri((f) => ({ ...f, campagna_id: id || '' }))} />
      ) : (<>
      {/* I contatori sono anche il modo piu' naturale di filtrare: cliccare
          "Non risolte 40" vale quanto scegliere lo stato nella barra qui sotto. */}
      {mostraElenco && (
        <div className="camp-conta">
          {STATI.map((s) => (
            <button key={s.key} type="button" title={spiegazioneStato(s.key)}
              className={`camp-chip${filtri.stato === s.key ? ' sel' : ''}${contatori[s.key] ? '' : ' vuoto'}`}
              style={{ '--chip': coloreStato(s.key) }}
              onClick={() => clickStato(s.key)}>
              <span className="camp-chip-n">{fmt(contatori[s.key])}</span> {s.label}
            </button>
          ))}
        </div>
      )}

      {mostraElenco && (
        <CampagnaFiltri filtri={filtri} onCambia={setFiltri} onAzzera={azzera}
          province={sugg.province} tipiImpresa={sugg.tipiImpresa}
          mostraTipo={mostraTipo} mostraCerved={mostraCerved} motiviCerved={motiviCerved} />
      )}

      {campagneStato === 'errore' ? (
        <div className="camp-msg camp-msg-err">
          Non riesco a leggere le campagne.{' '}
          <button type="button" className="camp-azzera"
            onClick={() => caricaCampagne()}>Riprova</button>
        </div>
      ) : campagneStato === 'carico' ? (
        <div className="camp-msg">Carico le liste…</div>
      ) : campagne.length === 0 ? (
        <div className="camp-vuoto">
          <b>Nessuna lista caricata.</b>
          <span>
            Le campagne sono gli elenchi che TIM consegna col numero troncato
            (098425***): si caricano da un file CSV e le righe restano qui finché il
            numero non viene ricostruito.
          </span>
          <button type="button" className="btn btn-primary" onClick={() => setCaricaOpen(true)}>
            Carica la prima campagna
          </button>
        </div>
      ) : stato === 'carico' ? (
        <div className="camp-msg">Carico le righe…</div>
      ) : stato === 'errore' ? (
        <div className="camp-msg camp-msg-err">Non sono riuscito a leggere le righe.</div>
      ) : righe.length === 0 ? (
        <div className="camp-msg">
          {attivi > 0 ? (
            <>
              Il filtro non trova nessuna riga.{' '}
              <button type="button" className="camp-azzera" onClick={azzera}>Azzera i filtri</button>
            </>
          ) : filtri.campagna_id ? 'Questa campagna non ha righe.'
            : 'Nessuna delle liste caricate ha righe da mostrare.'}
        </div>
      ) : (
        <>
          <div className="camp-conteggio">
            {fmt(righe.length)} righe mostrate di {fmt(totale)}
          </div>

          <CampagnaAzioni
            selezionate={scelte.size} tuttiFiltrati={tuttiFiltrati}
            totale={totale} mostrate={righe.length} tuttaPagina={tuttaPagina}
            onTutteFiltrate={() => { setScelte(new Set()); setTuttiFiltrati(true); }}
            onSoloMostrate={() => scegliTutte(true)}
            onAnnulla={annullaSelezione}
            onAzione={agisci} azione={azione} />
          <div className="camp-tabwrap">
            <table className="camp-tab">
              <thead>
                <tr>
                  <th className="camp-cb">
                    <input type="checkbox" checked={tuttiFiltrati || tuttaPagina}
                      title="Seleziona le righe mostrate"
                      onChange={(e) => scegliTutte(e.target.checked)} />
                  </th>
                  <th>Ragione sociale</th>
                  <th>Comune (Prov)</th>
                  {/* in una lista da import il numero non e' mascherato: e' il cellulare
                      che il file aveva, e chiamarlo «mascherato» sarebbe falso */}
                  <th>{eGenerica(campagnaScelta) ? 'Numero dalla lista' : 'Numero mascherato'}</th>
                  <th>P.IVA</th>
                  <th>Stato</th>
                  <th>Indagine</th>
                  <th>Trovato</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => {
                  const ident = etichettaIdentita(r.identita);
                  const accendibile = STATI_ATTIVABILI.includes(r.stato);
                  const numero = numeroMascherato(r);
                  const cerved = eCerved(r);
                  const generica = eGenerica(r);
                  return (
                    <tr key={r.id} className={rigaAperta === r.id ? 'sel' : ''}
                      onClick={() => setRigaAperta(r.id)}>
                      <td className="camp-cb" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={tuttiFiltrati || scelte.has(r.id)}
                          onChange={() => scegli(r.id)} />
                      </td>
                      <td className="camp-rs" title={r.ragione_sociale}>{r.ragione_sociale || '—'}</td>
                      <td className="camp-luogo" title={`${r.comune || ''} ${r.provincia || ''}`.trim()}>
                        {r.comune || '—'}
                        {r.provincia ? <span className="camp-prov"> ({r.provincia})</span> : null}
                      </td>
                      {/* Una riga CERVED il numero non ce l'ha per definizione: lo cerca
                          l'indagine sul sito e lo conferma con la partita IVA. */}
                      {cerved ? (
                        <td className="camp-num"
                          title="Lista CERVED: la riga non ha il numero. Lo cerca l’indagine sul sito dell’azienda e lo conferma con la partita IVA.">
                          <span className="camp-niente">—</span>
                          <span className="camp-tipo">CERVED</span>
                        </td>
                      ) : generica ? (
                        /* Riga arrivata dall'importazione: il numero che aveva non e'
                           verificabile dal 456 (un cellulare, o un troncone), e il fisso e'
                           proprio quello che l'indagine deve trovare. */
                        <td className="camp-num"
                          title="Riga da una lista importata: il numero che aveva non è una rete fissa. L’indagine cerca l’azienda e il suo fisso.">
                          {numeroDaLista(r) || <span className="camp-niente">—</span>}
                          <span className="camp-tipo">DA IMPORT</span>
                        </td>
                      ) : (
                        <td className="camp-num"
                          title={r.linea_contatto && r.linea_contatto !== numero
                            ? `Nel file: ${r.linea_contatto}` : undefined}>
                          {numero || '—'}
                        </td>
                      )}
                      <td className="camp-num">{r.partita_iva || '—'}</td>
                      <td>
                        <span className="camp-pill" style={{ '--pill': coloreStato(r.stato) }}
                          title={titoloStato(r.stato, r.motivo)}>
                          {etichettaStato(r.stato)}
                        </span>
                        {ident && (
                          <span className="camp-ident" title={ident.titolo} aria-label={ident.label}>!</span>
                        )}
                        {r.motivo ? <div className="camp-motivo">{etichettaMotivo(r.motivo)}</div> : null}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <label className={`camp-sw${accendibile ? '' : ' fermo'}`}
                          title={accendibile
                            ? 'Accendi o spegni l’indagine su questa riga'
                            : 'In questo stato l’indagine non si accende'}>
                          <input type="checkbox" checked={!!r.attiva} disabled={!accendibile}
                            onChange={(e) => cambiaAttiva(r, e.target.checked)} />
                          <span />
                        </label>
                      </td>
                      <td>
                        {r.numero_trovato
                          ? <span className="camp-trovato">{r.numero_trovato}</span>
                          : r.sito
                            ? <span className="camp-sito" title={r.sito}>{sitoCorto(r.sito)}</span>
                            : <span className="camp-niente">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {righe.length < totale && (
            <div className="camp-altre">
              <button type="button" className="btn btn-secondary" disabled={altre} onClick={caricaAltre}>
                {altre ? 'Carico…' : `Carica altre ${fmt(Math.min(PAGINA, totale - righe.length))}`}
              </button>
            </div>
          )}
        </>
      )}
      </>)}

      {/* Il dettaglio rilegge la riga per conto suo; qui torna solo quando e' cambiata
          davvero (promozione a mano), cosi' la tabella non resta indietro e i contatori,
          che il backend ricalcola dalle righe, si rifanno. */}
      <CampagnaRigaDialog
        rigaId={rigaAperta}
        onClose={() => setRigaAperta(null)}
        onCambiata={(nuova) => {
          if (nuova) setRighe((x) => x.map((y) => (y.id === nuova.id ? { ...y, ...nuova } : y)));
          caricaCampagne();
        }}
      />

      <CampagnaEliminaDialog
        campagna={eliminaOpen ? campagnaScelta : null}
        onClose={() => setEliminaOpen(false)}
        onEliminata={() => {
          // si torna a "tutte le campagne": quella scelta non esiste piu'
          setFiltri((f) => ({ ...f, campagna_id: '' }));
          caricaCampagne();
        }}
      />

    </div>
  );
}
